import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import type { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Shards } from '@sorare/core/src/atoms/icons/Shards';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { LabelL, LabelM, LabelS } from '@sorare/core/src/atoms/typography';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { usePaginatedQuery } from '@sorare/core/src/hooks/graphql/usePaginatedQuery';
import { getRarityColor } from '@sorare/core/src/lib/colors';

import type {
  CardPullEssenceBalanceQuery,
  CardPullEssenceBalanceQueryVariables,
} from './__generated__/index.graphql.ts';

const Illustration = styled.img`
  width: var(--triple-unit);
  height: var(--triple-unit);
`;

const Balance = styled(Vertical)`
  gap: var(--double-unit);
  background-color: var(--c-nd-50);
  border-radius: var(--double-unit);
  padding: var(--double-unit);
`;

const VerticalWithSeparator = styled(Vertical)`
  --gap: var(--double-unit);
  gap: var(--gap);

  > * + * {
    position: relative;
    &:before {
      content: '';
      position: absolute;
      top: calc(var(--gap) / -2);
      left: 0;
      right: 0;
      height: 1px;
      background-color: var(--c-nd-50);
    }
  }
`;

const QUERY = gql`
  query CardPullEssenceBalanceQuery(
    $rarity: Rarity!
    $sport: Sport!
    $after: String
  ) {
    currentUser {
      slug
      cardShardsHistoryTransactions(
        sport: $sport
        rarity: $rarity
        after: $after
        orderBy: ASC
        filterBy: UNSPENT_CREDIT
      ) {
        nodes {
          id
          date
          totalQuantity
          quantityByFlavour {
            slug
            count
            so5League {
              slug
              id
              iconUrl
              displayName
              shortDisplayName
            }
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
      blockchainCardShardsChests: cardShardsChests(
        sport: $sport
        rarities: [limited, rare, super_rare, unique]
      ) {
        id
        thresholds {
          threshold
        }
      }
    }
  }
` as TypedDocumentNode<
  CardPullEssenceBalanceQuery,
  CardPullEssenceBalanceQueryVariables
>;

type Props = {
  rarity: Rarity;
};

export const EssenceBalance = ({ rarity }: Props) => {
  const { sport } = useSportContext();

  const { data, loading, loadMore } = usePaginatedQuery(QUERY, {
    variables: { rarity, sport },
    connection: 'HistoryTransactionInterfaceConnection',
  });
  const { formatDistanceToNow } = useIntlContext();

  const paginatedResults = data?.currentUser?.cardShardsHistoryTransactions;

  const { threshold: cardPullCost } =
    data?.currentUser?.blockchainCardShardsChests[0].thresholds[0] ?? {};

  let remainingEssenceToPay = cardPullCost ?? 0;

  // fetch more data if needed
  if (
    !loading &&
    loadMore &&
    cardPullCost &&
    paginatedResults &&
    paginatedResults.nodes.reduce(
      (total, node) => total + node.totalQuantity,
      0
    ) < cardPullCost &&
    paginatedResults.pageInfo.hasNextPage
  ) {
    loadMore(false, { after: paginatedResults.pageInfo.endCursor });
  }
  if (loading) {
    return <LoadingIndicator />;
  }

  return (
    <Vertical gap={3}>
      <LabelM color="var(--c-nd-600)" className="text-center">
        <FormattedMessage
          defaultMessage="Each draw removes your oldest <b>{cardPullCost} Essences</b>"
          values={{
            cardPullCost,
            b: tokens => (
              <LabelL bold color="var(--c-red-400)">
                {tokens}
              </LabelL>
            ),
          }}
        />
      </LabelM>
      <Balance>
        <Vertical center gap={0}>
          <LabelL bold>
            <FormattedMessage defaultMessage="Essence Earning History" />
          </LabelL>
          <LabelS color="var(--c-nd-600)">
            <FormattedMessage defaultMessage="Oldest First" />
          </LabelS>
        </Vertical>
        <VerticalWithSeparator>
          {paginatedResults?.nodes.flatMap(transaction =>
            transaction.quantityByFlavour.map(flavour => {
              const paidEssence = Math.min(
                remainingEssenceToPay,
                flavour.count
              );
              remainingEssenceToPay -= paidEssence;
              return (
                <SBHorizontal key={`${transaction.id}-${flavour.slug}`}>
                  <Horizontal>
                    {!!flavour.so5League?.iconUrl && (
                      <Illustration
                        src={flavour.so5League.iconUrl}
                        alt={flavour.so5League.displayName}
                      />
                    )}
                    <Vertical gap={0}>
                      <LabelM bold>
                        {flavour.so5League?.shortDisplayName}
                      </LabelM>
                      <LabelS color="var(--c-nd-600)">
                        <FormattedMessage
                          defaultMessage="Won {timeAgo}"
                          values={{
                            timeAgo: formatDistanceToNow(transaction.date),
                          }}
                        />
                      </LabelS>
                    </Vertical>
                  </Horizontal>
                  <Horizontal>
                    <LabelM>{flavour.count}</LabelM>
                    {paidEssence > 0 && (
                      <LabelM color="var(--c-red-400)">-{paidEssence}</LabelM>
                    )}
                    <Shards color={getRarityColor(rarity)} />
                  </Horizontal>
                </SBHorizontal>
              );
            })
          )}
        </VerticalWithSeparator>
      </Balance>
    </Vertical>
  );
};
